<script setup lang="ts">
  import { ref, computed, watch, nextTick, useAttrs } from "vue";
  import {
    Select as SelectPrimitive,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select";
  import SelectShowMore from "./SelectShowMore.vue";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Input } from "@/components/ui/input";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { X, ChevronDown, ChevronUp, Check, Loader2, Search } from "lucide-vue-next";

  export interface SelectOption {
    value: string | number;
    label: string;
    badge?: string;
    disabled?: boolean;
    disabledHint?: string;
    separator?: boolean;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const attrs = useAttrs();

  const props = withDefaults(
    defineProps<{
      modelValue?: string | number | string[] | number[] | null;
      options?: SelectOption[];
      placeholder?: string;
      multiple?: boolean;
      filterable?: boolean;
      disabled?: boolean;
      remote?: boolean; // If true, filtering is handled remotely - component just emits search query
      loading?: boolean;
      hasMore?: boolean;
      creatable?: boolean;
      clearable?: boolean;
      variant?: "default" | "muted" | "pill";
      hideTags?: boolean;
      selectAll?: boolean;
      contentClass?: string;
    }>(),
    {
      modelValue: null,
      options: () => [],
      placeholder: "Select an option",
      multiple: false,
      filterable: false,
      disabled: false,
      remote: false,
      loading: false,
      hasMore: false,
      creatable: false,
      clearable: false,
      variant: "default",
      hideTags: false,
      selectAll: false,
      contentClass: "",
    },
  );

  const TRIGGER_VARIANTS = {
    default: "",
    muted: "border-transparent bg-muted shadow-none hover:bg-secondary",
    pill: "rounded-full bg-background font-semibold",
  };
  const triggerClass = computed(() => TRIGGER_VARIANTS[props.variant]);

  const emit = defineEmits<{
    "update:modelValue": [value: string | number | string[] | number[] | null];
    search: [query: string];
    "show-more": [];
    "open-change": [open: boolean];
  }>();

  const searchQuery = ref("");
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null);

  const seenOptions = new Map<string, SelectOption>();
  watch(
    () => props.options,
    (opts) => {
      for (const option of opts) seenOptions.set(String(option.value), option);
    },
    { immediate: true },
  );

  // For single select
  const selectedValues = computed(() => {
    if (!props.modelValue) return [];
    return props.multiple ? (Array.isArray(props.modelValue) ? props.modelValue : []) : [props.modelValue];
  });

  const filteredOptions = computed(() => {
    // When remote is enabled, don't filter locally - backend handles filtering
    if (props.remote) return props.options;

    if (!searchQuery.value) return props.options;

    const query = searchQuery.value.toLowerCase();

    return props.options.filter(
      (option) => !option.separator && (selectedValues.value.includes(option.value) || option.label.toLowerCase().includes(query)),
    );
  });

  const showCreateOption = computed(() => {
    if (!props.creatable || !searchQuery.value) return false;
    const query = searchQuery.value.toLowerCase();
    return !props.options.some((opt) => opt.label.toLowerCase() === query || String(opt.value) === searchQuery.value);
  });

  const selectedLabel = computed(() => {
    if (!selectedValues.value.length) return null;

    return selectedValues.value
      .map((val) => {
        const opt = props.options.find((o) => String(o.value) === String(val)) ?? seenOptions.get(String(val));
        return opt ? opt.label : val;
      })
      .join(", ");
  });

  // For multiple select with badges
  const selectedOptions = computed(() => {
    if (!props.multiple || !Array.isArray(props.modelValue) || !props.modelValue.length) return [];
    return props.modelValue.map((v) => {
      const found = props.options.find((o) => String(o.value) === String(v)) ?? seenOptions.get(String(v));
      return found ?? { value: String(v), label: String(v) };
    });
  });

  const focusSearch = () => {
    const root = searchInputRef.value?.$el as HTMLElement | undefined;
    const field = root instanceof HTMLInputElement ? root : root?.querySelector?.("input");
    field?.focus();
  };

  const isOpen = ref(false);

  const handleOpenChange = (open: boolean) => {
    isOpen.value = open;
    emit("open-change", open);
    if (open && props.filterable) {
      nextTick(focusSearch);
      return;
    }
    if (props.remote && searchQuery.value) emit("search", "");
    searchQuery.value = "";
  };

  const handleValueChange = (value: string | string[]) => {
    emit("update:modelValue", value);
    searchQuery.value = "";
  };

  const handleSearchKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") event.stopPropagation();
  };

  const handleSearchInput = (event) => {
    if (props.remote) {
      emit("search", event.target.value);
    }
  };

  const clearValue = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    emit("update:modelValue", props.multiple ? [] : null);
  };

  const clearSearch = () => {
    searchQuery.value = "";
    if (props.remote) emit("search", "");
    searchInputRef.value?.$el?.focus();
  };

  // Multiple select specific handlers
  const toggleValue = (value: string) => {
    if (!props.multiple) return;

    const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const stringValue = String(value);
    const index = currentValues.findIndex((v) => String(v) === stringValue);

    if (index === -1) {
      currentValues.push(value);
    } else {
      currentValues.splice(index, 1);
    }

    emit("update:modelValue", currentValues);
  };

  const removeValue = (value: string, event: Event) => {
    event.stopPropagation();
    if (props.disabled || !props.multiple) return;

    const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const stringValue = String(value);
    const filtered = currentValues.filter((v) => String(v) !== stringValue);
    emit("update:modelValue", filtered);
  };

  const createAndToggleValue = (value: string) => {
    toggleValue(value);
    searchQuery.value = "";
    if (props.remote) emit("search", "");
  };

  const isChecked = (value: string) => {
    if (!props.multiple || !Array.isArray(props.modelValue)) return false;
    return props.modelValue.some((v) => String(v) === String(value));
  };

  const matchesSearch = (option: SelectOption) =>
    props.remote || !searchQuery.value || option.label.toLowerCase().includes(searchQuery.value.toLowerCase());

  const selectableOptions = computed(() =>
    filteredOptions.value.filter((option) => !option.disabled && !option.separator && matchesSearch(option)),
  );
  const uncheckedOptions = computed(() =>
    selectableOptions.value.filter((option) => !isChecked(String(option.value))),
  );
  const areAllOptionsChecked = computed(() => !uncheckedOptions.value.length);

  const toggleAllOptions = () => {
    const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const visibleValues = selectableOptions.value.map((option) => String(option.value));

    if (areAllOptionsChecked.value) {
      const remainingValues = currentValues.filter((value) => !visibleValues.includes(String(value)));
      emit("update:modelValue", remainingValues as string[] | number[]);
      return;
    }

    const uncheckedValues = uncheckedOptions.value.map((option) => option.value);
    emit("update:modelValue", [...currentValues, ...uncheckedValues] as string[] | number[]);
  };
</script>

<template>
  <!-- Multiple select with badges UI -->
  <Popover
    v-if="multiple"
    @update:open="handleOpenChange">
    <PopoverTrigger
      :disabled="disabled"
      v-bind="attrs"
      class="flex min-h-8 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-2.5 py-1 text-13 shadow-xs transition-[color,box-shadow] focus:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/10 data-[state=open]:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
      :class="triggerClass">
      <div
        v-if="selectedOptions.length && !hideTags"
        class="flex flex-wrap gap-1 min-w-0 flex-1">
        <span
          v-for="option in selectedOptions"
          :key="option.value"
          class="inline-flex max-w-full items-center gap-1 rounded-md bg-secondary py-0.5 pr-1 pl-2 text-xs text-primary">
          <span class="min-w-0 truncate">{{ option.label }}</span>
          <button
            type="button"
            class="flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-none hover:bg-border-strong/40 hover:text-primary focus-visible:bg-border-strong/40"
            :disabled="disabled"
            @click="removeValue(option.value, $event)">
            <X
              class="size-3"
              :stroke-width="2.2" />
          </button>
        </span>
      </div>
      <span
        v-else
        class="truncate text-muted-foreground">
        {{ placeholder }}
      </span>
      <component
        :is="isOpen ? ChevronUp : ChevronDown"
        class="size-4 shrink-0 text-muted-foreground" />
    </PopoverTrigger>
    <PopoverContent
      class="w-[var(--reka-popover-trigger-width)] p-0 flex flex-col max-h-[360px] overflow-hidden"
      :class="contentClass"
      align="start">
      <!-- Search input for multiple + filterable -->
      <div
        v-if="filterable"
        class="sticky top-0 z-10 border-b bg-popover px-3">
        <div class="relative">
          <Input
            ref="searchInputRef"
            v-model="searchQuery"
            :icon="Search"
            placeholder="Search…"
            class="h-10 border-0 bg-transparent px-0 pr-8 shadow-none has-[:focus-visible]:ring-0 [&>svg]:text-foreground-subtle [&_input]:placeholder:text-foreground-subtle"
            @keydown="handleSearchKeydown"
            @input="handleSearchInput" />
          <Loader2
            v-if="loading"
            class="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          <Button
            v-else-if="searchQuery"
            variant="ghost"
            size="icon"
            class="absolute right-2 top-1/2 -translate-y-1/2 size-5 opacity-50 hover:opacity-100"
            @click="clearSearch">
            <X class="size-3" />
          </Button>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto py-1">
        <button
          v-if="showCreateOption"
          type="button"
          class="flex w-full items-center gap-2 px-3 py-2 text-13 outline-none hover:bg-accent focus:bg-accent"
          @click.stop.prevent="createAndToggleValue(searchQuery)">
          <span class="min-w-0 flex-1 truncate text-left">Create "{{ searchQuery }}"</span>
        </button>
        <button
          v-if="selectAll && selectableOptions.length"
          type="button"
          class="mb-1 flex w-full items-center gap-2 border-b px-3 py-2 text-13 font-medium text-foreground outline-none hover:bg-accent focus:bg-accent"
          @click.stop.prevent="toggleAllOptions">
          <span class="min-w-0 flex-1 truncate text-left">
            {{ areAllOptionsChecked ? "Deselect all" : `Select all (${uncheckedOptions.length})` }}
          </span>
        </button>
        <button
          v-for="option in filteredOptions"
          :key="option.value"
          type="button"
          :title="option.label"
          :disabled="option.disabled"
          class="flex w-full items-center gap-2 px-3 py-2 text-13 outline-none hover:bg-accent focus:bg-accent disabled:pointer-events-none disabled:opacity-50"
          :class="isChecked(option.value) ? 'font-semibold text-primary' : 'text-foreground'"
          @click.stop.prevent="toggleValue(option.value)">
          <span class="min-w-0 flex-1 truncate text-left">{{ option.label }}</span>
          <Check
            v-if="isChecked(option.value)"
            class="size-4 shrink-0" />
        </button>
        <SelectShowMore
          v-if="remote && hasMore && filteredOptions.length"
          :loading="loading"
          @show-more="emit('show-more')" />
        <div
          v-if="!filteredOptions?.length && !showCreateOption"
          class="px-2 py-4 text-sm text-muted-foreground text-center">
          No options
        </div>
      </div>
    </PopoverContent>
  </Popover>

  <!-- Single select UI -->
  <div
    v-else
    v-bind="attrs">
    <SelectPrimitive
      :disabled="disabled"
      :modal="false"
      :model-value="modelValue as string"
      @update:model-value="handleValueChange"
      @open-change="handleOpenChange">
    <SelectTrigger :class="[clearable && selectedValues.length ? 'group/clearable' : '', triggerClass]">
      <SelectValue
        :placeholder="placeholder"
        class="min-w-0 flex-1">
        <span
          v-if="selectedLabel"
          class="truncate block">
          {{ selectedLabel }}
        </span>
      </SelectValue>
      <button
        v-if="clearable && selectedValues.length"
        type="button"
        class="flex shrink-0 items-center justify-center rounded-sm opacity-40 hover:opacity-100"
        @pointerdown.stop.prevent="clearValue"
        @click.stop.prevent>
        <X class="size-3" />
      </button>
    </SelectTrigger>
    <SelectContent>
      <div
        v-if="filterable"
        class="sticky top-0 z-10 bg-popover p-2 border-b">
        <div class="relative">
          <Input
            ref="searchInputRef"
            v-model="searchQuery"
            placeholder="Search..."
            class="h-9 pr-8"
            @keydown="handleSearchKeydown"
            @input="handleSearchInput" />
          <Loader2
            v-if="loading"
            class="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          <Button
            v-else-if="searchQuery"
            variant="ghost"
            size="icon"
            class="absolute right-2 top-1/2 -translate-y-1/2 size-5 opacity-50 hover:opacity-100"
            @click="clearSearch">
            <X class="size-3" />
          </Button>
        </div>
      </div>
      <div class="max-h-[300px] overflow-y-auto">
        <div
          v-if="loading && !filteredOptions.length"
          class="py-6 text-center text-sm text-muted-foreground">
          Loading...
        </div>
        <div
          v-else-if="filteredOptions.length === 0 && !showCreateOption"
          class="py-6 text-center text-sm text-muted-foreground">
          No results found
        </div>
        <template v-else>
          <SelectItem
            v-if="showCreateOption"
            :value="searchQuery"
            :title="`Create &quot;${searchQuery}&quot;`">
            Create "{{ searchQuery }}"
          </SelectItem>
          <template
            v-for="option in filteredOptions"
            :key="option.value">
            <SelectSeparator v-if="option.separator" />
            <Tooltip
              v-else-if="option.disabled && option.disabledHint"
              :content="option.disabledHint"
              side="right"
              trigger-class="!cursor-not-allowed">
              <div class="cursor-not-allowed">
                <SelectItem
                  :value="option.value"
                  :title="option.label"
                  disabled>
                  {{ option.label }}
                </SelectItem>
              </div>
            </Tooltip>
            <SelectItem
              v-else
              :value="option.value"
              :title="option.label"
              :disabled="option.disabled">
              {{ option.label }}
              <Badge
                v-if="option.badge"
                variant="secondary"
                class="ml-1.5 text-xs font-normal align-middle">
                {{ option.badge }}
              </Badge>
            </SelectItem>
          </template>
          <SelectShowMore
            v-if="remote && hasMore && filteredOptions.length"
            :loading="loading"
            @show-more="emit('show-more')" />
        </template>
      </div>
    </SelectContent>
  </SelectPrimitive>
  </div>
</template>
